import { memo, useCallback, useMemo, useState } from 'react';
import { useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { useSelector } from '@bonanza-social/toolkit/state';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import { Trans } from '@bonanza-social/i18n';
import {
  Box,
  Text,
  Image,
  Button,
  AnimationPlayer,
  useLayout,
} from '@bonanza-social/moonlight';
import { selectStatus } from '../../state/status/status.slice';
import { COMMON_STYLES, ONBOARDING_STEPS } from './StatusOnboarding.content';
import {
  OnboardingStep,
  StatusOnboardingProps,
} from './StatusOnboarding.types';
import animations from './../../assets/animations/onboarding';
import StatusGemsLineAnimation from './StatusGemsLineAnimation/StatusGemsLineAnimation';

function StatusOnboarding({
  onInitialAnimationFinished,
  ...rest
}: StatusOnboardingProps) {
  const [step, setStep] = useState<OnboardingStep>();
  const [layout, onLayout] = useLayout();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const status = useSelector(selectStatus);
  const textStyle = useAnimatedStyle(
    () => ({
      opacity: withTiming(Number(step !== undefined), { duration: 800 }),
    }),
    [step],
  );
  const stepStyle = useAnimatedStyle(
    () => ({
      transform: [
        {
          translateX: withTiming(
            step ? -ONBOARDING_STEPS.indexOf(step) * layout.width : 0,
          ),
        },
      ],
    }),
    [step, layout.width],
  );

  const handleInitialAnimationFinished = useCallback(() => {
    onInitialAnimationFinished?.();
    setStep(ONBOARDING_STEPS[0]);
  }, [onInitialAnimationFinished]);

  const goToNextStep = useCallback(() => {
    setStep((step) => {
      if (!step) {
        return;
      }

      const stepIndex = ONBOARDING_STEPS.indexOf(step);

      return ONBOARDING_STEPS[stepIndex + 1];
    });
  }, []);

  const goToPreviousStep = useCallback(() => {
    setStep((step) => {
      if (!step) {
        return;
      }

      const stepIndex = ONBOARDING_STEPS.indexOf(step);

      return ONBOARDING_STEPS[Math.max(0, stepIndex - 1)];
    });
  }, []);

  const config = useMemo(
    () => ({
      [OnboardingStep.statuses]: {
        getContent: () => <StatusGemsLineAnimation />,
        onPrimaryPress: goToNextStep,
        onSecondaryPress: navigation.goBack,
      },
      [OnboardingStep.playNEarn]: {
        getContent: () =>
          status &&
          status.playerStatus !== 'Member' && (
            <Box maxHeight="100%" margin="auto">
              <Image
                name={`status.${status.playerStatus}Gem`}
                contentFit="cover"
                maxHeight="100%"
                width={167}
                height={215}
              />
              <Image
                name="common.BonanzaCoinStack"
                position="absolute"
                right="-space-400"
                bottom="-space-400"
                maxHeight="100%"
                width={121}
                height={121}
              />
            </Box>
          ),
        onPrimaryPress: goToNextStep,
        onSecondaryPress: goToPreviousStep,
      },
      [OnboardingStep.keepProgressing]: {
        getContent: () => (
          <Image
            name="status.Hourglass"
            maxHeight="100%"
            margin="auto"
            width={179}
            height={179}
          />
        ),
        onPrimaryPress: goToNextStep,
        onSecondaryPress: goToPreviousStep,
      },
      [OnboardingStep.gettingBetter]: {
        getContent: () => (
          <Image
            name="status.Perks"
            maxHeight="100%"
            margin="auto"
            width={262}
            height={254}
          />
        ),
        onPrimaryPress: () => {
          navigation.goBack();
          navigation.navigate('StatusPromotion');
        },
        onSecondaryPress: goToPreviousStep,
      },
    }),
    [status, goToNextStep, goToPreviousStep, navigation],
  );

  return (
    <Box {...rest}>
      {!step ? (
        <Box absoluteFill>
          <AnimationPlayer
            loop={false}
            sizeX="100%"
            sizeY="100%"
            source={animations.gemsFlying}
            onComplete={handleInitialAnimationFinished}
          />
        </Box>
      ) : (
        <Box flex={1} alignItems="center" animatedStyle={textStyle}>
          <Box gap="space-300" {...COMMON_STYLES}>
            <Text
              variant="overline"
              locNS="status"
              locKey="onboardingTitle"
              color="darkSurfacePrimary"
              textAlign="center"
            />
            <Text
              variant="h3"
              locNS="status"
              locKey="onboardingSubtitle"
              color="darkSurfacePrimary"
              textAlign="center"
            />
          </Box>
          <Box
            flex={1}
            width="100%"
            flexDirection="row"
            animatedStyle={stepStyle}
            onLayout={onLayout}
          >
            {ONBOARDING_STEPS.map((step) => (
              <Box key={step} overflow="hidden" width="100%">
                {config[step].getContent()}
              </Box>
            ))}
          </Box>
          <Box {...COMMON_STYLES}>
            <Text
              variant="body-m-semi"
              locNS="status"
              locKey={`onboarding_${step}_step_title`}
              color="darkSurfacePrimary"
              textAlign="center"
              padding="space-300"
              paddingBottom="space-400"
            />
            <Text
              variant="body-s"
              color="darkSurfacePrimary"
              textAlign="center"
              paddingBottom="space-400"
              paddingHorizontal="space-300"
            >
              <Trans
                ns="status"
                i18nKey={`onboarding_${step}_step_description`}
                components={{
                  b: <Text variant="body-s-semi" />,
                }}
              />
            </Text>
            <Button
              variant="primary"
              locNS="status"
              locKey={`onboarding_${step}_step_primary_button`}
              marginTop="space-600"
              marginBottom="space-200"
              onPress={config[step].onPrimaryPress}
            />
            <Button
              variant="secondary"
              locNS="status"
              locKey={`onboarding_${step}_step_secondary_button`}
              onPress={config[step].onSecondaryPress}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default memo(StatusOnboarding);
